<template>
    <div class="donut-chart">
        <svg
            :viewBox="`0 0 ${width} ${height}`"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlns:xlink="http://www.w3.org/1999/xlink"
            @dblclick.prevent="download"
            ref="svg"
        >
            <g class="donut" :transform="`translate(${width / 2}, ${height / 2})`">
                <g v-for="slice in slices" :key="slice.start">
                    <path
                        class="donut-slice"
                        :d="slice.path"
                        :stroke="slice.color"
                        :fill="slice.color"
                        :title="slice.datum.label"
                        @mouseover="e => $emit('mouseover', slice, e)"
                        @mouseout="e => $emit('mouseout', slice, e)"
                    />
                </g>
                <slot v-bind="{ slices, hole, radius, width, height }" name="default" />
            </g>
        </svg>
        <slot v-bind="{ slices, width, height }" name="append" />
    </div>
</template>
<script>
import { computePadding, pieColors } from './utils'
import { download as browserDownload } from '@/utils/download.js'
import { getPngFromOuterHTML } from '@/utils/svg.js'

export { pieColors as colors }

function createPath(startAnglePercentage, endAnglePercentage, radius, hole) {
    // hacky way to deal with the degenerate case of a full circle
    // svg can't handle a path that is a full circle so just split
    // everything into two bits which svg can handle
    function cos(pct) {
        return Math.cos(pct * Math.PI * 2 - Math.PI / 2)
    }

    function sin(pct) {
        return Math.sin(pct * Math.PI * 2 - Math.PI / 2)
    }

    const midpoint = (endAnglePercentage + startAnglePercentage) / 2

    const holeArcX1 = (cos(startAnglePercentage) * hole).toFixed(5)
    const holeArcY1 = (sin(startAnglePercentage) * hole).toFixed(5)

    // need this for maths
    const holeArcX2 = cos(midpoint) * hole
    const holeArcY2 = sin(midpoint) * hole

    const holeArcX3 = (cos(endAnglePercentage) * hole).toFixed(5)
    const holeArcY3 = (sin(endAnglePercentage) * hole).toFixed(5)

    const radiusArcX1 = (cos(startAnglePercentage) * radius).toFixed(5)
    const radiusArcY1 = (sin(startAnglePercentage) * radius).toFixed(5)

    // need this for maths
    const radiusArcX2 = cos(midpoint) * radius
    const radiusArcY2 = sin(midpoint) * radius

    const radiusArcX3 = (cos(endAnglePercentage) * radius).toFixed(5)
    const radiusArcY3 = (sin(endAnglePercentage) * radius).toFixed(5)

    hole = hole.toFixed(5)
    radius = radius.toFixed(5)

    return {
        path:
            `M ${holeArcX3},${holeArcY3} ` +
            `A ${hole},${hole},1,0,0,${holeArcX2.toFixed(5)},${holeArcY2.toFixed(5)} ` +
            `A ${hole},${hole},1,0,0,${holeArcX1},${holeArcY1} ` +
            `L ${radiusArcX1},${radiusArcY1} ` +
            `A ${radius},${radius},0,0,1,${radiusArcX2.toFixed(5)},${radiusArcY2.toFixed(5)} ` +
            `A ${radius},${radius},0,0,1,${radiusArcX3},${radiusArcY3} ` +
            'Z',
        innerpoint: { x: holeArcX2, y: holeArcY2 },
        centerpoint: { x: (holeArcX2 + radiusArcX2) / 2, y: (holeArcY2 + radiusArcY2) / 2 },
        outerpoint: { x: radiusArcX2, y: radiusArcY2 },
    }
}

export default {
    props: {
        dataset: {
            type: Array,
            default() {
                return []
            },
        },
        hole: {
            type: Number,
            default: 100,
        },
        padding: {
            type: [Number, Object, String],
            default: 20,
        },
        radius: {
            type: Number,
            default: 200,
        },
    },
    computed: {
        computedPadding() {
            return computePadding(this.padding)
        },
        height() {
            return this.radius * 2 + this.computedPadding.top + this.computedPadding.bottom
        },
        slices() {
            let autoColor = 0
            let start = 0
            return this.dataset
                .filter(datum => datum.value > 0)
                .map(datum => {
                    const percentage = datum.value / this.total
                    const end = start + percentage
                    const val = {
                        datum,
                        color: datum.color ?? pieColors[autoColor++ % pieColors.length],
                        start,
                        end,
                        percentage,
                        ...createPath(start, end, this.radius, this.hole),
                    }
                    start = end
                    return val
                })
        },
        total() {
            return this.dataset.reduce((acc, curr) => acc + curr.value, 0)
        },
        width() {
            return this.radius * 2 + this.computedPadding.left + this.computedPadding.right
        },
    },
    methods: {
        async download() {
            browserDownload(await getPngFromOuterHTML(this.$refs.svg, 480, 480), 'donut-chart.png')
        },
    },
}
</script>
